import { useCallback, useMemo } from 'react';
import type { Document } from '@rossum/api-client/documents';
import type { Page } from '@rossum/api-client/pages';
import { Stack } from '@rossum/ui/material';
import {
  type Band,
  BandForeignObject,
  type PageId,
  type PageLayoutData,
} from '../document-canvas';
import {
  CancelChangesDialog,
  type EditDocumentConfig,
  type EditState,
  hasChangesToConfirm,
  useEditState,
} from '../document-edit-core';
import {
  AFTER_PAGE_BAND_HEIGHT,
  HEADER_BAND_HEIGHT,
  OriginalDocumentHeader,
  PageNumberBadge,
  SplitCanvas,
  SplitCanvasProvider,
} from '../document-split-canvas';
import { SplitSidebar } from './document-sidebar/SplitSidebar';
import { SplitPageNavigation } from './document-topbar/SplitPageNavigation';
import { SplitTopBar } from './document-topbar/SplitTopBar';
import { createSplitPageIndex } from './session/pageKeys';
import { useSplitExit } from './session/useSplitExit';
import type { UseSplitSessionResult } from './session/useSplitSession';
import { useUndoShortcut } from './session/useUndoShortcut';

const SPLIT_CANVAS_BANDS = {
  header: HEADER_BAND_HEIGHT,
  afterPage: AFTER_PAGE_BAND_HEIGHT,
};

const renderAfterPage = (
  pageModel: Page,
  _layout: PageLayoutData,
  band: Band
) => (
  <BandForeignObject band={band}>
    <PageNumberBadge pageNumber={pageModel.number} />
  </BandForeignObject>
);

type SplitScreenReadyProps = {
  config: EditDocumentConfig;
  document: Document | undefined;
  pages: Page[];
  workspaceName: string | undefined;
  queueName: string | undefined;
  confirm: UseSplitSessionResult['confirm'];
  confirmInProgress: boolean;
  onConfirmError: (error: unknown, editState: EditState) => void;
};

export const SplitScreenReady = ({
  config,
  document,
  pages,
  workspaceName,
  queueName,
  confirm,
  confirmInProgress,
  onConfirmError,
}: SplitScreenReadyProps) => {
  const [editState, dispatchEdit] = useEditState(config);
  const hasChanges = hasChangesToConfirm(editState);

  const { handleLeave, cancelDialogOpen, closeCancelDialog, confirmLeave } =
    useSplitExit({ hasChanges });

  useUndoShortcut({
    enabled: !cancelDialogOpen && !confirmInProgress,
    dispatchEdit,
  });

  const documentName = document?.originalFileName ?? undefined;

  const pageIndex = useMemo(() => createSplitPageIndex(pages), [pages]);

  const { pageRotations, deletedPageIds } = useMemo(() => {
    const rotations: Record<PageId, number> = {};
    const deleted: PageId[] = [];

    editState.parts
      .flatMap(part => part.pages)
      .forEach(page => {
        const id = pageIndex.idByNumber[page.pageNumber];

        if (id === undefined) return;
        rotations[id] = page.rotationDeg;
        if (page.deleted) deleted.push(id);
      });

    return { pageRotations: rotations, deletedPageIds: deleted };
  }, [editState.parts, pageIndex]);

  const renderHeader = useCallback(
    (band: Band) => (
      <BandForeignObject band={band}>
        <OriginalDocumentHeader
          workspaceName={workspaceName}
          queueName={queueName}
          documentName={documentName}
        />
      </BandForeignObject>
    ),
    [workspaceName, queueName, documentName]
  );

  return (
    <SplitCanvasProvider>
      <Stack
        sx={{ height: '100%', width: '100%' }}
        data-cy="split-screen-ready"
      >
        <SplitTopBar
          documentName={documentName}
          onLeave={handleLeave}
          pageNavigation={
            <SplitPageNavigation
              pageIndex={pageIndex}
              totalPages={pages.length}
            />
          }
        />
        <Stack
          direction="row"
          sx={{
            width: '100%',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <SplitSidebar
            onConfirm={() =>
              confirm(editState, {
                onError: error => onConfirmError(error, editState),
              })
            }
            confirmInProgress={confirmInProgress}
            hasChanges={hasChanges}
          />
          <Stack
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              height: '100%',
              position: 'relative',
            }}
            data-cy="split-screen-canvas"
          >
            <SplitCanvas
              pages={pages}
              bands={SPLIT_CANVAS_BANDS}
              renderHeader={renderHeader}
              renderAfterPage={renderAfterPage}
              pageRotations={pageRotations}
              deletedPageIds={deletedPageIds}
            />
          </Stack>
        </Stack>
      </Stack>
      <CancelChangesDialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        onCancel={confirmLeave}
      />
    </SplitCanvasProvider>
  );
};
